/**
 * Admin helpers for jurisdiction prescription PDF preview.
 */

/**
 * Builds the preview request body from the current unsaved form values.
 * @param {Record<string, unknown>} form
 * @returns {{ regulation: Record<string, unknown> }}
 */
export function previewPayload(form) {
  const countryCode = String(form.countryCode || "")
    .trim()
    .toUpperCase();
  return {
    regulation: {
      ...form,
      countryCode,
    },
  };
}

/**
 * Prefer a human-readable API error when axios received a JSON error as a blob.
 * @param {unknown} err
 * @returns {Promise<string>}
 */
export async function previewErrorMessage(err) {
  const data = err && typeof err === "object" ? err.response?.data : null;
  if (data instanceof Blob) {
    try {
      const text = await data.text();
      const parsed = JSON.parse(text);
      const msg = parsed?.error || parsed?.message;
      if (msg != null && String(msg).trim()) return String(msg).trim();
    } catch {
      // Fall through to generic message.
    }
  }
  if (err && typeof err === "object" && err.response?.data) {
    const body = err.response.data;
    if (typeof body === "object" && body !== null) {
      const msg = body.error || body.message;
      if (msg != null && String(msg).trim()) return String(msg).trim();
    }
  }
  if (err && typeof err === "object" && err.message && !String(err.message).startsWith("Please allow")) {
    const status = err.response?.status;
    if (status) return `Unable to open the PDF preview (${status}). Please try again.`;
  }
  return "Unable to open the PDF preview. Please try again.";
}

/**
 * Requests a preview PDF and opens it in a new browser tab.
 * Do not pass noopener in windowFeatures: browsers return null for the WindowProxy
 * even when the tab opens, which falsely looks like a blocked pop-up.
 *
 * @param {{ post: Function }} http
 * @param {Record<string, unknown>} body
 * @param {Window} [win]
 * @returns {Promise<void>}
 */
export async function openPreview(http, body, win = window) {
  const tab = win.open("", "_blank");
  if (!tab) {
    throw new Error("Please allow pop-ups to view the PDF preview.");
  }
  try {
    // Isolate from opener after we have a usable handle (same-origin blank tab).
    try {
      tab.opener = null;
    } catch {
      // Ignore if the browser forbids writing opener.
    }
    const res = await http.post("/api/v1/prescription-regulations/preview", body, {
      responseType: "blob",
      suppressServerErrorSwal: true,
      suppressInlineErrorSwal: true,
    });
    const blob = new Blob([res.data], { type: "application/pdf" });
    const url = win.URL.createObjectURL(blob);
    tab.location.replace(url);
    win.setTimeout(() => {
      win.URL.revokeObjectURL(url);
    }, 60000);
  } catch (err) {
    if (typeof tab.close === "function") tab.close();
    const message = await previewErrorMessage(err);
    throw new Error(message);
  }
}
